import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  PaDataTable,
  PaEmptyState,
  PaSkeleton,
  PaStatusBadge,
} from "components/platform-admin";
import { platformRoleAccess } from "helpers/RolesPermission";
import { fetchPlatformTenantIntegrations } from "store/services/platformAdmin.services";
import { buildIntegrationRows } from "./tenantIntegrations.utils";

const TenantIntegrationsTab = ({ tenantId }) => {
  const canViewIntegrations = platformRoleAccess("platform.integrations.view");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [integrations, setIntegrations] = useState(null);

  useEffect(() => {
    if (!canViewIntegrations || !tenantId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadIntegrations = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchPlatformTenantIntegrations(tenantId);
        if (!cancelled) {
          setIntegrations(data?.integrations ?? {});
        }
      } catch (err) {
        if (!cancelled) {
          setIntegrations({});
          setError(err?.message || "Failed to load integration health");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadIntegrations();

    return () => {
      cancelled = true;
    };
  }, [canViewIntegrations, tenantId]);

  const rows = useMemo(() => buildIntegrationRows(integrations), [integrations]);

  const columns = useMemo(
    () => [
      {
        key: "label",
        header: "Provider",
        render: (row) => <span className="font-medium text-gray-900">{row.label}</span>,
      },
      {
        key: "status",
        header: "Status",
        render: (row) => (
          <PaStatusBadge label={row.statusBadge.label} variant={row.statusBadge.variant} />
        ),
      },
      {
        key: "detail",
        header: "Connection",
        render: (row) => <span className="text-gray-600">{row.detail || "—"}</span>,
      },
      {
        key: "connectedAt",
        header: "Connected",
        render: (row) =>
          row.connectedAt ? moment(row.connectedAt).format("D MMM YYYY") : "—",
      },
    ],
    []
  );

  if (!canViewIntegrations) {
    return (
      <PaEmptyState
        title="Access restricted"
        message="You don't have access to integration health"
        description="You need platform.integrations.view to view payroll connection status."
      />
    );
  }

  if (loading) {
    return <PaSkeleton variant="table" lines={4} />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <h3 className="text-sm font-semibold text-gray-900">Payroll integrations</h3>
        <p className="mt-1 text-sm text-gray-500">
          View-only connection status for Xero, QuickBooks, and MYOB. Does not refresh tokens
          or mutate tenant connections.
        </p>
      </div>
      <PaDataTable columns={columns} rows={rows} emptyMessage="No integration data available." />
    </div>
  );
};

export default TenantIntegrationsTab;
